import React, { useCallback, useState } from 'react'
import {
  CustomizeFormItemProps,
  FormItem,
  FormItemType,
} from 'packages/components/FormItem'
import { QuizTypeEnum } from 'packages/sdks-next/growp'
import styled from 'styled-components'
import { Form, InputNumber, Modal } from '@seiue/ui'
import { ExerciseSetting } from '@/uikit/QuotePaper/ExerciseSetting'
import { FormInstance } from 'antd'

/**
 * 测验-基础设置
 * @param param - 参数
 * @param param.isEdit - 是否是编辑模式
 * @param param.paperId - 选中的试卷ID
 * @param param.isNormalModal - 是否是普通模式
 * @param param.setPaperId - 设置选中的试卷ID
 * @param param.setIsNormalModal - 设置测验模式
 * @param param.setTitle - 设置测验标题
 * @returns React.ReactNode
 */
export const BaseSetting: React.FC<{
  form: FormInstance
  isEdit: boolean
  paperId?: number
  isNormalMode: boolean
  setPaperId: (v?: number) => void
  setIsNormalMode: (v: boolean) => void
  setTitle: (v: string) => void
  userIsSubmit?: boolean
}> = ({
  form,
  paperId,
  setPaperId,
  isNormalMode,
  setIsNormalMode,
  setTitle,
  userIsSubmit,
  isEdit,
}) => {
  const [blockCount, setBlockCount] = useState<number>(0)
  const formConfigs: CustomizeFormItemProps[] = [
    {
      type: FormItemType.Input,
      formItemConfig: {
        label: '测验标题',
        name: 'name',
        required: true,
        rules: [
          {
            required: true,
            message: '请填写测验标题',
          },
        ],
      },
      componentConfig: {
        inputProps: {
          maxLength: 200,
          onChange: (e) => {
            setTitle(e.target.value)
          },
          placeholder: '请输入测验标题',
        },
      },
    },
    {
      type: FormItemType.TextArea,
      formItemConfig: {
        label: '测验简介',
        name: 'description',
      },
      componentConfig: {
        textAreaProps: {
          maxLength: 500,
          placeholder: '请输入测验简介',
          autoSize: {
            minRows: 4,
          },
        },
      },
    },
    {
      type: FormItemType.Radio,
      formItemConfig: {
        label: '测验模式',
        name: 'type',
        rules: [
          {
            required: true,
            message: '请填写测验标题',
          },
        ],
      },
      componentConfig: {
        radioGroupProps: {
          onChange: (e) => {
            if (!paperId) {
              setIsNormalMode(e.target.value === QuizTypeEnum.Normal)
              return
            }
            Modal.confirm({
              title: '确定切换测验模式吗？',
              content:
                '正在切换测验模式，已创建的试卷数据将被清空，确认切换吗？',
              onOk: () => {
                setPaperId(undefined)
                setIsNormalMode(e.target.value === QuizTypeEnum.Normal)
              },
              onCancel: () => {
                form.setFieldValue(
                  'type',
                  e.target.value === QuizTypeEnum.Normal
                    ? QuizTypeEnum.Rand
                    : QuizTypeEnum.Normal
                )
              },
            })
          },
          options: [
            {
              label: '普通模式',
              value: QuizTypeEnum.Normal,
            },
            {
              label: '抽题模式',
              value: QuizTypeEnum.Rand,
            },
          ],
          disabled: isEdit,
        },
      },
    },
    {
      type: FormItemType.Number,
      formItemConfig: {
        label: '测验时长',
        name: 'minuteLimit',
        required: true,
        rules: [
          {
            required: true,
            message: '请填写测验时长',
          },
        ],
      },
      componentConfig: {
        inputNumberProps: {
          addonAfter: <span>分钟</span>,
          style: {
            width: '120px',
          },
          max: 1440,
          min: 1,
          disabled: userIsSubmit,
        },
      },
    },
    {
      hidden: !isNormalMode,
      type: FormItemType.Radio,
      formItemConfig: {
        label: '显示姓名',
        name: 'showName',
        extra: '是否在批阅时显示学生的姓名',
      },
      componentConfig: {
        radioGroupProps: {
          options: [
            {
              label: '是',
              value: true,
            },
            {
              label: '否',
              value: false,
            },
          ],
        },
      },
    },
    {
      type: FormItemType.Radio,
      formItemConfig: {
        label: '答案展示',
        name: 'showAnswer',
        extra: '是否在答题结束后展示答案解析',
      },
      componentConfig: {
        radioGroupProps: {
          options: [
            {
              label: '是',
              value: true,
            },
            {
              label: '否',
              value: false,
            },
          ],
        },
      },
    },
    {
      type: FormItemType.Radio,
      formItemConfig: {
        label: '反作弊',
        name: 'againstCheat',
        extra: '启用后学生需要开启相机功能才能作答，可监测成员考试情况',
      },
      componentConfig: {
        radioGroupProps: {
          options: [
            {
              label: '启用',
              value: true,
            },
            {
              label: '不启用',
              value: false,
            },
          ],
          disabled: userIsSubmit,
        },
      },
    },
  ]

  const getLimitLevel = useCallback(() => {
    if (isNormalMode && userIsSubmit) {
      return 4
    }
    return 0
  }, [isNormalMode, userIsSubmit])
  return (
    <>
      {formConfigs.map((config, index) => {
        return (
          <React.Fragment key={`${index}`}>
            {config.formItemConfig.name === 'type' ? (
              <ModeSettingWrapper>
                <FormItem {...config} />
                <div
                  style={{
                    marginLeft: '90px',
                  }}
                >
                  <ExerciseSetting
                    value={paperId}
                    onChange={(v) => setPaperId(v)}
                    isRand={!isNormalMode}
                    setBlockCount={(v) => setBlockCount(v)}
                    isQuiz={true}
                    limitLevel={getLimitLevel()}
                  />
                </div>

                {!isNormalMode ? (
                  <PaperSetting>
                    <Form.Item
                      name="questionCount"
                      label="出题数量"
                      labelCol={{ span: 24 }}
                      rules={[
                        {
                          required: true,
                          message: `${
                            blockCount ? `请输入出题数量` : '请先创建试卷'
                          }`,
                        },
                      ]}
                    >
                      <InputNumber
                        step={1}
                        precision={0}
                        addonAfter="个"
                        min={1}
                        max={blockCount}
                      />
                    </Form.Item>
                    <Form.Item
                      name="passScoreRate"
                      label="通过正确率"
                      labelCol={{ span: 24 }}
                      tooltip="若测验成绩的正确率没有达到要求，则需反复答题，直至达到通过要求为止"
                      rules={[
                        {
                          required: true,
                          message: `${
                            blockCount ? `请输入正确率` : '请先创建试卷'
                          }`,
                        },
                        {
                          pattern: /^(?:[1-9][0-9]?|100)$/,
                          message: '正确率应>0',
                        },
                      ]}
                    >
                      <InputNumber
                        addonAfter="%"
                        precision={0}
                        max={100}
                        min={0.1}
                      />
                    </Form.Item>
                  </PaperSetting>
                ) : null}
              </ModeSettingWrapper>
            ) : (
              <FormItem {...config} />
            )}
          </React.Fragment>
        )
      })}
    </>
  )
}

const PaperSetting = styled.div`
  margin-left: 88px;
  display: flex;
  justify-content: flex-start;
  &&& {
    .ant-form-item {
      min-width: 106px;
      max-width: 116px;
    }
    .ant-form-item-no-colon {
      height: 22px;
    }
    .ant-form-item-tooltip > svg {
      fill: ${(props) => props.theme.text._4};
    }
    .ant-input-number-group-wrapper {
      width: 96px;
    }
    .ant-input-number-handler-wrap {
      display: none;
    }
    .ant-form-item-label
      > label.ant-form-item-required:not(
        .ant-form-item-required-mark-optional
      )::before {
      display: none;
    }
  }
`

const ModeSettingWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
`
